import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Register = ({ onSwitchToLogin, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownTimer = useRef(null);

  const { register, sendVerificationCode, checkPhoneExists, checkUsernameExists, useServerAuth } = useAuth();

  useEffect(() => {
    return () => {
      if (countdownTimer.current) {
        clearInterval(countdownTimer.current);
      }
    };
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
    countdownTimer.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSendCode = async () => {
    if (!formData.phoneNumber) {
      setError('请先输入手机号');
      return;
    }

    // 手机号格式验证
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('请输入正确的手机号格式');
      return;
    }

    // 检查手机号是否已存在
    if (!useServerAuth && checkPhoneExists(formData.phoneNumber)) {
      setError('该手机号已被注册');
      return;
    }

    setSendingCode(true);
    setError('');

    try {
      const result = await sendVerificationCode(formData.phoneNumber);
      if (result.success) {
        setSuccess('验证码已发送，请查收短信');
        // 开始倒计时
        startCountdown();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('发送验证码失败，请稍后重试');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // 基本验证
    if (!formData.name || !formData.phoneNumber || !formData.password || !formData.verificationCode) {
      setError('请填写所有必填字段');
      setLoading(false);
      return;
    }

    // 手机号格式验证
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('请输入正确的手机号格式');
      setLoading(false);
      return;
    }

    // 邮箱格式验证（如果填写了邮箱）
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('请输入正确的邮箱格式');
        setLoading(false);
        return;
      }
    }

    // 密码长度验证
    if (formData.password.length < 8) {
      setError('密码长度至少8位');
      setLoading(false);
      return;
    }

    // 确认密码验证
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    // 姓名长度验证
    if (formData.name.length < 2 || formData.name.length > 20) {
      setError('姓名长度应在2-20个字符之间');
      setLoading(false);
      return;
    }

    // 本地模式检查显示名是否已存在
    if (!useServerAuth && checkUsernameExists(formData.name)) {
      setError('该姓名已被使用');
      setLoading(false);
      return;
    }

    try {
      const result = await register(formData);
      if (result.success) {
        setSuccess('注册成功！正在为您登录...');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
        </div>
        <h2 className="text-3xl font-serif font-bold text-sage-800 mb-2">加入我们</h2>
        <p className="text-sage-600">创建您的瑜伽社区账号</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-base font-semibold text-sage-700 mb-3">
            姓名 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="请输入姓名"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              maxLength="20"
            />
          </div>
          <div className="mt-2 text-sm text-sage-600">
            2-20个字符，将作为您的显示名称
          </div>
        </div>

        <div>
          <label htmlFor="phoneNumber" className="block text-base font-semibold text-sage-700 mb-3">
            手机号 <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="请输入11位手机号"
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                maxLength="11"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel"
              />
            </div>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sendingCode || countdown > 0}
              className="w-full px-6 py-4 bg-gradient-to-r from-sage-600 to-emerald-600 text-white text-lg rounded-xl hover:from-sage-700 hover:to-emerald-700 focus:outline-none focus:ring-3 focus:ring-sage-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
            >
              {sendingCode ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
            </button>
          </div>
          <div className="mt-2 text-sm text-sage-600">
            我们将向您的手机发送验证码
          </div>
        </div>

        <div>
          <label htmlFor="verificationCode" className="block text-base font-semibold text-sage-700 mb-3">
            验证码 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <input
              type="text"
              id="verificationCode"
              name="verificationCode"
              value={formData.verificationCode}
              onChange={handleChange}
              placeholder="请输入6位验证码"
              className="w-full pl-12 pr-4 py-4 text-lg tracking-widest border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              maxLength="6"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-base font-semibold text-sage-700 mb-3">
            邮箱 <span className="text-gray-400 text-xs">(可选)</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="请输入邮箱（可选）"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              autoComplete="email"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-base font-semibold text-sage-700 mb-3">
            密码 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="请输入密码（至少8位）"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-base font-semibold text-sage-700 mb-3">
            确认密码 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="请再次输入密码"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              autoComplete="new-password"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 text-green-700 px-4 py-3 rounded-r-lg text-sm flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-6 rounded-lg hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              注册中...
            </div>
          ) : '立即注册'}
        </button>
      </form>

      <div className="mt-8 text-center">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-sage-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-sage-500">已有账号？</span>
          </div>
        </div>
        <button
          onClick={onSwitchToLogin}
          className="mt-4 text-sage-600 hover:text-sage-800 font-semibold transition-colors duration-200 hover:underline"
        >
          立即登录 →
        </button>
      </div>
    </div>
  );
};

export default Register;
